import { supabase } from '../config/supabase';
import {
  ProfessionalPaymentRecord,
  PeriodType,
} from '../types/professional-payment';

interface ConfirmProfessionalPaymentInput {
  clientId: string;
  doctorId: string;
  professionalName: string;
  startDatePeriod: Date;
  endDatePeriod: Date;
  paymentTotalSessions: number;
  paymentTotalAmount: number;
  paymentSessionsWithEvolution: number;
  paymentMethod: string;
  paymentNotes: string;
  paymentPaidBy: string;
  paymentPaidAt: Date;
  appointmentIds: string[];
}

type PaymentAppointmentLinkRow = {
  paymentId: string;
  appointmentId: string;
};

type AppointmentWithPatientRow = {
  appointmentId: string;
  appointmentStartDate: string;
  appointmentTitle: string | null;
  appointmentLocation: string | null;
  appointmentEvolutionId: string | null;
  appointmentStatus: string | null;
  appointmentIsBilled: boolean | null;
  patients?: { patientFullName?: string | null } | { patientFullName?: string | null }[] | null;
};

const relationRows = <T,>(value: T | T[] | null | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

export class ProfessionalPaymentRecordService {
  private static deriveReportType(startDate: Date, endDate: Date): PeriodType {
    const sameMonth =
      startDate.getFullYear() === endDate.getFullYear() &&
      startDate.getMonth() === endDate.getMonth();
    const startsAtFirstDay = startDate.getDate() === 1;
    const lastDayOfMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();
    const endsAtLastDay = endDate.getDate() === lastDayOfMonth;

    return sameMonth && startsAtFirstDay && endsAtLastDay ? 'billing_period' : 'date_range';
  }

  private static formatReportPeriod(startDate: Date, endDate: Date, reportType: PeriodType): string {
    if (reportType === 'billing_period') {
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      return `${month}/${startDate.getFullYear()}`;
    }

    const formatDate = (date: Date) => date.toLocaleDateString('pt-BR');
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }

  private static mapRecord(record: any): ProfessionalPaymentRecord {
    const paymentStartDatePeriod = new Date(record.paymentStartDatePeriod);
    const paymentEndDatePeriod = new Date(record.paymentEndDatePeriod);
    const paymentPaidAt = record.paymentPaidAt ? new Date(record.paymentPaidAt) : undefined;
    const reportType = this.deriveReportType(paymentStartDatePeriod, paymentEndDatePeriod);
    const reportPeriod = this.formatReportPeriod(paymentStartDatePeriod, paymentEndDatePeriod, reportType);

    return {
      paymentId: record.paymentId,
      clientId: record.clientId,
      doctorId: record.doctorId,
      paymentProfessionalName: record.paymentProfessionalName,
      paymentStartDatePeriod,
      paymentEndDatePeriod,
      paymentTotalSessions: record.paymentTotalSessions,
      paymentTotalAmount: record.paymentTotalAmount,
      paymentSessionsWithEvolution: record.paymentSessionsWithEvolution,
      paymentIsPaid: record.paymentIsPaid,
      paymentPaidAt,
      paymentPaidBy: record.paymentPaidBy,
      paymentMethod: record.paymentMethod,
      paymentNotes: record.paymentNotes,
      paymentCreatedAt: new Date(record.paymentCreatedAt),
      paymentUpdatedAt: new Date(record.paymentUpdatedAt),

      // Compatibilidade com campos legados ainda usados em telas/serviços
      id: record.paymentId,
      professionalName: record.paymentProfessionalName,
      startDate: paymentStartDatePeriod,
      endDate: paymentEndDatePeriod,
      totalSessions: record.paymentTotalSessions,
      totalAmount: record.paymentTotalAmount,
      sessionsWithEvolution: record.paymentSessionsWithEvolution,
      isPaid: record.paymentIsPaid,
      paidAt: paymentPaidAt,
      notes: record.paymentNotes || '',
      reportType,
      reportPeriod,
      appointmentDetails: [],
    };
  }

  private static formatDateAndTime(dateValue: string): { date: string; time: string } {
    const dateObj = new Date(dateValue);
    if (Number.isNaN(dateObj.getTime())) {
      return { date: '-', time: '-' };
    }

    return {
      date: dateObj.toLocaleDateString('pt-BR'),
      time: dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    };
  }

  private static async enrichRecordsWithAppointmentDetails(
    records: ProfessionalPaymentRecord[],
    clientId?: string
  ): Promise<ProfessionalPaymentRecord[]> {
    const paymentIds = records
      .map((record) => record.paymentId)
      .filter((paymentId): paymentId is string => Boolean(paymentId));

    if (paymentIds.length === 0) {
      return records;
    }

    let linksQuery = supabase
      .from('professional_payment_appointments')
      .select('paymentId, appointmentId')
      .in('paymentId', paymentIds);

    if (clientId) {
      linksQuery = linksQuery.eq('clientId', clientId);
    }

    const { data: linksData, error: linksError } = await linksQuery;
    if (linksError) {
      console.warn('Erro ao carregar vínculos de atendimentos dos repasses:', linksError);
      return records;
    }

    const links = (linksData || []) as PaymentAppointmentLinkRow[];
    if (links.length === 0) {
      return records;
    }

    const appointmentIds = Array.from(
      new Set(links.map((link) => link.appointmentId).filter(Boolean))
    );

    if (appointmentIds.length === 0) {
      return records;
    }

    const { data: appointmentsData, error: appointmentsError } = await supabase
      .from('appointments')
      .select(`
        appointmentId,
        appointmentStartDate,
        appointmentTitle,
        appointmentLocation,
        appointmentEvolutionId,
        appointmentStatus,
        appointmentIsBilled,
        patients ( patientFullName )
      `)
      .in('appointmentId', appointmentIds);

    if (appointmentsError) {
      console.warn('Erro ao carregar detalhes de atendimentos dos repasses:', appointmentsError);
      return records;
    }

    const appointmentsById = new Map<string, AppointmentWithPatientRow>();
    for (const appointment of (appointmentsData || []) as AppointmentWithPatientRow[]) {
      appointmentsById.set(appointment.appointmentId, appointment);
    }

    const linksByPayment = new Map<string, string[]>();
    for (const link of links) {
      const existing = linksByPayment.get(link.paymentId) || [];
      existing.push(link.appointmentId);
      linksByPayment.set(link.paymentId, existing);
    }

    return records.map((record) => {
      const currentPaymentId = record.paymentId;
      if (!currentPaymentId) {
        return record;
      }

      const linkedAppointmentIds = linksByPayment.get(currentPaymentId) || [];
      if (linkedAppointmentIds.length === 0) {
        return record;
      }

      const sessionValue =
        record.paymentTotalSessions > 0
          ? Number((record.paymentTotalAmount / record.paymentTotalSessions).toFixed(2))
          : 0;

      const appointmentDetails = linkedAppointmentIds
        .map((appointmentId) => appointmentsById.get(appointmentId))
        .filter((appointment): appointment is AppointmentWithPatientRow => Boolean(appointment))
        .sort(
          (a, b) =>
            new Date(a.appointmentStartDate).getTime() - new Date(b.appointmentStartDate).getTime()
        )
        .map((appointment) => {
          const patientRows = relationRows(appointment.patients);
          const patientName =
            patientRows[0]?.patientFullName ||
            appointment.appointmentTitle ||
            'Paciente não identificado';
          const { date, time } = this.formatDateAndTime(appointment.appointmentStartDate);

          return {
            id: appointment.appointmentId,
            date,
            time,
            patientName,
            description: appointment.appointmentTitle || '',
            location: appointment.appointmentLocation || '-',
            hasEvolution: Boolean(appointment.appointmentEvolutionId),
            evolutionId: appointment.appointmentEvolutionId || undefined,
            isBilled: Boolean(appointment.appointmentIsBilled),
            status: appointment.appointmentStatus || 'completed',
            value: sessionValue,
          };
        });

      return {
        ...record,
        appointmentDetails,
      };
    });
  }

  private static async findExistingRecordForPeriod(
    clientId: string,
    doctorId: string,
    paymentStartDatePeriod: string,
    paymentEndDatePeriod: string
  ): Promise<{ paymentId: string; paymentIsPaid: boolean } | null> {
    const { data, error } = await supabase
      .from('professional_payment_records')
      .select('paymentId, paymentIsPaid')
      .eq('clientId', clientId)
      .eq('doctorId', doctorId)
      .eq('paymentStartDatePeriod', paymentStartDatePeriod)
      .eq('paymentEndDatePeriod', paymentEndDatePeriod)
      .order('paymentCreatedAt', { ascending: false })
      .limit(1);

    if (error) throw error;
    if (!data || data.length === 0) return null;

    return {
      paymentId: data[0].paymentId,
      paymentIsPaid: data[0].paymentIsPaid,
    };
  }

  static async createPaymentRecord(
    clientId: string,
    doctorId: string,
    paymentProfessionalName: string,
    paymentStartDatePeriod: Date,
    paymentEndDatePeriod: Date,
    paymentTotalSessions: number,
    paymentTotalAmount: number,
    paymentSessionsWithEvolution: number,
  ): Promise<string> {
    try {
      const paymentStartDateISO = paymentStartDatePeriod.toISOString();
      const paymentEndDateISO = paymentEndDatePeriod.toISOString();

      const existingRecord = await this.findExistingRecordForPeriod(
        clientId,
        doctorId,
        paymentStartDateISO,
        paymentEndDateISO
      );

      if (existingRecord) {
        if (existingRecord.paymentIsPaid) {
          throw new Error('Este repasse já foi confirmado para o período selecionado.');
        }
        return existingRecord.paymentId;
      }

      const { data, error } = await supabase
        .from('professional_payment_records')
        .insert({
          clientId,
          doctorId,
          paymentProfessionalName,
          paymentStartDatePeriod: paymentStartDateISO,
          paymentEndDatePeriod: paymentEndDateISO,
          paymentTotalSessions,
          paymentTotalAmount,
          paymentSessionsWithEvolution,
          paymentIsPaid: false,
        })
        .select('paymentId');

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Falha ao criar registro de pagamento: Nenhum ID retornado.');

      return data[0].paymentId; // Retorna o ID do registro criado
    } catch (error) {
      // Em caso de conflito de unicidade (409/23505), tenta reaproveitar o registro já existente.
      if (
        typeof error === 'object' &&
        error !== null &&
        ('code' in error || 'status' in error)
      ) {
        const errorCode = 'code' in error ? String(error.code) : '';
        const errorStatus = 'status' in error ? Number(error.status) : 0;

        if (errorCode === '23503') {
          throw new Error('Não foi possível registrar o pagamento: o profissional não está vinculado à tabela de doutores.');
        }

        if (errorCode === '23505' || errorStatus === 409) {
          try {
            const existingRecord = await this.findExistingRecordForPeriod(
              clientId,
              doctorId,
              paymentStartDatePeriod.toISOString(),
              paymentEndDatePeriod.toISOString()
            );

            if (existingRecord) {
              if (existingRecord.paymentIsPaid) {
                throw new Error('Este repasse já foi confirmado para o período selecionado.');
              }
              return existingRecord.paymentId;
            }
          } catch {
            // Segue para o tratamento padrão logo abaixo.
          }
        }
      }

      console.error('Error creating payment record:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erro ao criar registro de pagamento');
    }
  }

  static async confirmPaymentWithAppointments(
    input: ConfirmProfessionalPaymentInput
  ): Promise<string> {
    try {
      const appointmentIds = Array.from(new Set(input.appointmentIds.filter(Boolean)));
      if (appointmentIds.length === 0) {
        throw new Error('Nenhum atendimento elegível foi encontrado para confirmar o repasse.');
      }

      const { data, error } = await supabase.rpc('confirm_professional_payment', {
        p_client_id: input.clientId,
        p_doctor_id: input.doctorId,
        p_professional_name: input.professionalName,
        p_start_date_period: input.startDatePeriod.toISOString(),
        p_end_date_period: input.endDatePeriod.toISOString(),
        p_payment_total_sessions: input.paymentTotalSessions,
        p_payment_total_amount: input.paymentTotalAmount,
        p_payment_sessions_with_evolution: input.paymentSessionsWithEvolution,
        p_payment_method: input.paymentMethod,
        p_payment_notes: input.paymentNotes || '',
        p_payment_paid_by: input.paymentPaidBy,
        p_payment_paid_at: input.paymentPaidAt.toISOString(),
        p_appointment_ids: appointmentIds,
      });

      if (error) {
        const dbMessage = String(error.message || '').toLowerCase();
        if (error.code === '23505' || dbMessage.includes('já foram') || dbMessage.includes('conflito')) {
          throw new Error('Uma ou mais sessões desse relatório já foram repassadas. Atualize o relatório e tente novamente.');
        }
        throw error;
      }

      if (!data) {
        throw new Error('Não foi possível confirmar o pagamento do repasse.');
      }

      if (appointmentIds.length > 0) {
        const { error: linkError } = await supabase
          .from('appointments')
          .update({
            professionalPaymentRecordId: String(data),
            appointmentUpdatedAt: new Date().toISOString(),
          })
          .in('appointmentId', appointmentIds)
          .eq('clientId', input.clientId);

        if (linkError) {
          throw linkError;
        }
      }

      return String(data);
    } catch (error) {
      console.error('Error confirming professional payment with appointments:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erro ao confirmar pagamento do repasse.');
    }
  }

  static async markAsPaid(
    paymentId: string,
    paymentMethod: string,
    paymentNotes: string,
    paymentPaidBy: string,
    clientId: string,
    paymentPaidAt?: Date
  ): Promise<void> {
    try {
      if (!clientId) {
        throw new Error('ClientId é obrigatório para marcar pagamento como pago.');
      }

      const { data: existingRecord, error: fetchError } = await supabase
        .from('professional_payment_records')
        .select('paymentIsPaid')
        .eq('paymentId', paymentId)
        .eq('clientId', clientId)
        .single();

      if (fetchError) throw fetchError;
      if (!existingRecord) {
        throw new Error('Registro de pagamento não encontrado');
      }
      if (existingRecord.paymentIsPaid) {
        throw new Error('Este pagamento já foi marcado como pago');
      }

      const { error: updateError } = await supabase
        .from('professional_payment_records')
        .update({
          paymentIsPaid: true,
          paymentPaidAt: paymentPaidAt ? paymentPaidAt.toISOString() : new Date().toISOString(),
          paymentPaidBy,
          paymentMethod,
          paymentNotes: paymentNotes || "",
          paymentUpdatedAt: new Date().toISOString(),
        })
        .eq('paymentId', paymentId)
        .eq('clientId', clientId);

      if (updateError) throw updateError;
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      throw error;
    }
  }

  static async undoPayment(
    paymentId: string,
    _userId: string,
    clientId: string
  ): Promise<void> {
    try {
      if (!clientId) {
        throw new Error('ClientId é obrigatório para desfazer pagamento.');
      }

      const { data: existingRecord, error: fetchError } = await supabase
        .from('professional_payment_records')
        .select('paymentIsPaid, paymentPaidBy, paymentPaidAt')
        .eq('paymentId', paymentId)
        .eq('clientId', clientId)
        .single() as { data: { paymentIsPaid: boolean; paymentPaidBy: string; paymentPaidAt: string | null } | null; error: any };

      if (fetchError) throw fetchError;
      if (!existingRecord) {
        throw new Error('Registro de pagamento não encontrado');
      }
      if (!existingRecord.paymentIsPaid) {
        throw new Error('Este pagamento não está marcado como pago');
      }

      const { error: releaseLinksError } = await supabase
        .from('professional_payment_appointments')
        .delete()
        .eq('paymentId', paymentId)
        .eq('clientId', clientId);

      if (releaseLinksError) throw releaseLinksError;

      await supabase
        .from('appointments')
        .update({
          professionalPaymentRecordId: null,
          appointmentUpdatedAt: new Date().toISOString(),
        })
        .eq('clientId', clientId)
        .eq('professionalPaymentRecordId', paymentId);

      const { error: updateError } = await supabase
        .from('professional_payment_records')
        .update({
          paymentIsPaid: false,
          paymentPaidAt: null,
          paymentPaidBy: null,
          paymentMethod: null,
          paymentNotes: null,
          paymentUpdatedAt: new Date().toISOString(),
        })
        .eq('paymentId', paymentId)
        .eq('clientId', clientId);

      if (updateError) throw updateError;
    } catch (error) {
      console.error('Error undoing payment:', error);
      throw error;
    }
  }

  static async deletePaymentRecord(paymentId: string, _userId: string, clientId: string): Promise<void> {
    try {
      if (!clientId) {
        throw new Error('ClientId é obrigatório para apagar pagamento.');
      }

      // Antes de deletar, podemos buscar o registro para fins de log ou validação, se necessário.
      // Por enquanto, vamos direto ao ponto da exclusão.
      const { error } = await supabase
        .from('professional_payment_records')
        .delete()
        .eq('paymentId', paymentId)
        .eq('clientId', clientId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting payment record:', error);
      throw error;
    }
  }

  static async getPaymentHistory(
    filters?: {
      clientId?: string;
      doctorId?: string;
      paymentIsPaid?: boolean;
      paymentStartDatePeriod?: Date;
      paymentEndDatePeriod?: Date;
    }
  ): Promise<ProfessionalPaymentRecord[]> {
    try {
      let queryBuilder = supabase
        .from("professional_payment_records")
        .select("*", { count: "exact" });

      if (filters?.clientId) {
        queryBuilder = queryBuilder.eq("clientId", filters.clientId);
      }

      if (filters?.doctorId) {
        queryBuilder = queryBuilder.eq("doctorId", filters.doctorId);
      }

      if (filters?.paymentIsPaid !== undefined) {
        queryBuilder = queryBuilder.eq("paymentIsPaid", filters.paymentIsPaid);
      }

      if (filters?.paymentStartDatePeriod) {
        queryBuilder = queryBuilder.gte(
          "paymentStartDatePeriod",
          filters.paymentStartDatePeriod.toISOString()
        );
      }

      if (filters?.paymentEndDatePeriod) {
        // Ajusta a data final para o início do próximo dia (exclusive boundary)
        const endDateAdjusted = new Date(filters.paymentEndDatePeriod);
        endDateAdjusted.setDate(endDateAdjusted.getDate() + 1);
        endDateAdjusted.setHours(0, 0, 0, 0);

        queryBuilder = queryBuilder.lt(
          "paymentEndDatePeriod",
          endDateAdjusted.toISOString()
        );
      }

      queryBuilder = queryBuilder.order("paymentCreatedAt", { ascending: false });

      const { data, error } = await queryBuilder;

      if (error) throw error;

      const mappedRecords = data.map((record) => this.mapRecord(record));
      return await this.enrichRecordsWithAppointmentDetails(mappedRecords, filters?.clientId);
    } catch (error) {
      console.error("Error getting payment history:", error);
      throw new Error("Erro ao buscar histórico de pagamentos");
    }
  }

  static async getPaymentsByProfessional(
    doctorId: string,
    clientId?: string
  ): Promise<ProfessionalPaymentRecord[]> {
    return this.getPaymentHistory({ doctorId, clientId });
  }

  static async getPaymentsByStatus(
    paymentIsPaid: boolean,
    clientId?: string
  ): Promise<ProfessionalPaymentRecord[]> {
    return this.getPaymentHistory({ paymentIsPaid, clientId });
  }

  static async getPaymentById(
    paymentId: string,
    clientId: string
  ): Promise<ProfessionalPaymentRecord | null> {
    try {
      if (!clientId) {
        return null;
      }

      const { data: record, error: fetchError } = await supabase
        .from('professional_payment_records')
        .select('*')
        .eq('paymentId', paymentId)
        .eq('clientId', clientId)
        .single();

      if (fetchError) throw fetchError;
      if (!record) return null;

      const mappedRecord = this.mapRecord(record);
      const enrichedRecords = await this.enrichRecordsWithAppointmentDetails([mappedRecord], clientId);
      return enrichedRecords[0] || mappedRecord;
    } catch (error) {
      console.error('Error getting payment by id:', error);
      throw new Error('Erro ao buscar registro de pagamento');
    }
  }

  // Função de auditoria removida ou a ser implementada de forma diferente para Supabase
  private static async createAuditLog(
    paymentId: string,
    action: string,
    userId: string,
    metadata: Record<string, any>
  ): Promise<void> {
    console.warn(`Audit log para paymentId ${paymentId} - Ação: ${action} - Usuário: ${userId} - Metadata:`, metadata);
    // Implementar lógica de auditoria para Supabase se necessário, por exemplo, inserindo em uma tabela de logs
  }

  static async getPendingPaymentsSummary(clientId?: string): Promise<{
    totalPending: number;
    totalAmount: number;
    professionalCount: number;
  }> {
    try {
      const pendingRecords = await this.getPaymentsByStatus(false, clientId);

      const totalAmount = pendingRecords.reduce(
        (sum, record) => sum + record.paymentTotalAmount,
        0
      );

      const uniqueProfessionals = new Set(
        pendingRecords.map((r) => r.doctorId)
      );

      return {
        totalPending: pendingRecords.length,
        totalAmount,
        professionalCount: uniqueProfessionals.size,
      };
    } catch (error) {
      console.error('Error getting pending payments summary:', error);
      throw new Error('Erro ao calcular resumo de pagamentos pendentes');
    }
  }

  static async getPaidPaymentsForPeriod(
    startDate: Date,
    endDate: Date,
    clientId?: string
  ): Promise<{
    totalPaid: number;
    totalAmount: number;
  }> {
    try {
      const allPaidRecords = await this.getPaymentsByStatus(true, clientId);

      // Ajusta a data final para o início do próximo dia (exclusive boundary)
      const endDateAdjusted = new Date(endDate);
      endDateAdjusted.setDate(endDateAdjusted.getDate() + 1);
      endDateAdjusted.setHours(0, 0, 0, 0);

      const recordsInPeriod = allPaidRecords.filter((record) => {
        if (!record.paymentPaidAt) return false;
        const paidDate = record.paymentPaidAt;
        return paidDate >= startDate && paidDate < endDateAdjusted;
      });

      const totalAmount = recordsInPeriod.reduce(
        (sum, record) => sum + record.paymentTotalAmount,
        0
      );

      return {
        totalPaid: recordsInPeriod.length,
        totalAmount,
      };
    } catch (error) {
      console.error('Error getting paid payments for period:', error);
      throw new Error('Erro ao calcular pagamentos do período');
    }
  }
}
